/* ─────────────────────────────────────────────────────────────
   Everything the shell needs to know about this site.

   The three company sites and the group portal all carry a file of
   this shape. Keeping the nav, the footer, the pledge, the FAQ and
   the reviews here rather than inline in JSX is what lets the entire
   section layer be identical across the four properties: a component
   never names a page, only a key in this file.
   ───────────────────────────────────────────────────────────── */

import Logo from '../media/images/smart-electric-solutions-white-logo.png';

import groupLogo from '../media/images/smart-solutions-group-logo.png';
import techLogo from '../media/images/smart-technology-solutions-white-logo.png';
import constructionLogo from '../media/images/smart-construction-solutions-white-logo.png';

import { ReactComponent as WiringIcon } from '../media/images/svg/wiring.svg';
import { ReactComponent as VoltageIcon } from '../media/images/svg/voltage.svg';
import { ReactComponent as PowerIcon } from '../media/images/svg/power.svg';
import { ReactComponent as MaintenanceIcon } from '../media/images/svg/maintenance.svg';
import { ReactComponent as EnergyEfficiencyIcon } from '../media/images/svg/energy_efficiency.svg';
import { ReactComponent as SafetyIcon } from '../media/images/svg/safety.svg';
import { ReactComponent as DataCenterIcon } from '../media/images/svg/data_center.svg';
import { ReactComponent as ManufacturingIcon } from '../media/images/svg/manufacturing.svg';

export const site = {
    id: 'electric',
    name: 'Smart Electric Solutions',
    short: 'SES',
    discipline: 'High voltage electrical',
    origin: 'https://smartelectric.solutions',
    logo: Logo,
    tagline: 'Powering what comes next.',
    blurb:
        'A full-service residential and commercial electrical contractor ' +
        'serving South Florida, bringing a century-old profession into the ' +
        '21st century, and one of the three companies that make up ' +
        'The Smart Solutions Group.'
};

export const contact = {
    phone: '+1 (305) 205-6409',
    phoneHref: 'tel:+13052056409',
    email: 'info@smartelectric.solutions',
    address: '3040 N 29th Ave #1, Hollywood, FL 33020',
    addressHref: 'https://maps.app.goo.gl/R9s8ojY9xfTxPXQk8',
    instagram: 'https://www.instagram.com/ses_smart_electric_solutions/',
    serviceArea:
        'Palm Beach County, Broward County and Miami-Dade County, and everywhere in between.'
};

/* The nav, in the order it is read.

   All three company sites carry the same bar: two dropdowns, the two
   flat links a visitor goes looking for by name, and the dashboard.

       Solutions ▾   what we build, beside where we build it
       Services ▾    how the work is bought and supported
       Portfolio     proof
       About         who we are
       Dashboard ↗   our own app, and the one item that leaves

   `group` is the dropdown an item sits in; `section` is the column it
   sits in inside that panel, and doubles as the `eyebrow` and breadcrumb
   on the page itself, which is what keeps the bar and the page agreeing
   about where the reader is. An item with neither sits flat on the bar.

   A group holding one destination renders as a flat link rather than a
   dropdown, so a site with a single Services page still reads like its
   sisters. Footer builds its link columns from `section`, so a change
   here moves both.

   This site carries two flat links its sisters do not. The TPI
   partnership has a `badge`, the short tag set beside an item on the
   bar and in the sheet. It is for news; take the key off once the
   partnership stops being new and the link stays as an ordinary one.
   The SmartDC demo sits beside the dashboard because it is the same kind of
   destination: something to go and use rather than read about. */
export const menu = [
    { name: 'Power Systems',      href: '/power-systems',      group: 'Solutions', section: 'Systems' },
    { name: 'Lighting Solutions', href: '/lighting-solutions', group: 'Solutions', section: 'Systems' },
    { name: 'Energy Efficiency',  href: '/energy-efficiency',  group: 'Solutions', section: 'Systems' },
    { name: 'Safety Standards',   href: '/safety-standards',   group: 'Solutions', section: 'Systems' },
    { name: 'SmartDC Monitoring', href: '/data-center-monitoring', group: 'Solutions', section: 'Systems' },

    { name: 'Residential Services', href: '/residential-services', group: 'Solutions', section: 'Spaces' },
    { name: 'Commercial Services',  href: '/commercial-services',  group: 'Solutions', section: 'Spaces' },

    { name: 'Services Overview',      href: '/services',               group: 'Services', section: 'Services' },
    { name: 'Electrical Maintenance', href: '/electrical-maintenance', group: 'Services', section: 'Services' },

    { name: 'Portfolio', href: '/portfolio' },
    { name: 'About',     href: '/about' },
    { name: 'TPI Partnership', short: 'TPI', href: '/tpi', badge: 'New' },
    { name: 'SmartDC Demo', href: '/data-center-monitoring/demo' },
    {
        name: 'Smart Building Dashboard',
        short: 'Dashboard',
        href: 'https://building-dashboard.smart-tech.solutions',
        external: true,
        companion: true
    }
];

export const trades = [
    'High Voltage', 'Panel Upgrades', 'Switchgear', 'Fire Alarm', 'Generators',
    'Solar & PV', 'EV Charging', 'LED Retrofits', 'Landscape Lighting',
    'Service Calls', 'Code Corrections', 'Surge Protection', 'Energy Audits',
    'New Construction', 'Mid & High Rise'
];

export const statement =
    'A century-old trade, run on modern systems. Design, estimating, procurement ' +
    'and field execution on one connected platform, so the crew on your job is ' +
    'working from the same information the office is.';

/* The disciplines, as chips in the hero. Icon and label are the pairing
   the site has always used; each one now also goes somewhere. Manufacturing
   is a sector with no page of its own yet, so it lands on the page closest
   to the work. */
export const disciplines = [
    { label: 'Wiring & Cabling',   href: '/residential-services',   Icon: WiringIcon },
    { label: 'Voltage Systems',    href: '/commercial-services',    Icon: VoltageIcon },
    { label: 'Smart Power',        href: '/power-systems',          Icon: PowerIcon },
    { label: 'Maintenance',        href: '/electrical-maintenance', Icon: MaintenanceIcon },
    { label: 'Energy Efficiency',  href: '/energy-efficiency',      Icon: EnergyEfficiencyIcon },
    { label: 'Safety Inspections', href: '/safety-standards',       Icon: SafetyIcon },
    { label: 'Data Centers',       href: '/data-center-monitoring', Icon: DataCenterIcon },
    { label: 'Manufacturing',      href: '/commercial-services',    Icon: ManufacturingIcon }
];

/* Copy for the closing call to action, the process heading and the
   contact block. These used to be default arguments inside the shared
   Cta, ProcessRail and ContactSection components, so every site that did
   not pass props rendered whichever company those defaults happened to be
   written for: eight pages here, plus the home page's contact form, were
   offering to talk about framing and drywall. */
export const cta = {
    eyebrow: 'Start a project',
    title: 'Power it properly.',
    lead: 'One call covers the service, the distribution and the lighting. Tell us what you are building anywhere in South Florida.'
};

export const contactCopy = {
    eyebrow: 'Start a project',
    title: 'Let’s get the power right.',
    lead: 'Tell us what you are building or upgrading anywhere in South Florida. One conversation covers the service, the distribution and the trim.'
};

export const processHeading = {
    eyebrow: 'How it works',
    title: 'One contractor, from the meter in.'
};

export const pledge = [
    {
        lead: 'We',
        rest: 'Are',
        items: [
            'Licensed & Insured',
            'Certified Technicians',
            'Efficient & Responsive',
            'Service That Lasts',
            'Satisfaction Guaranteed'
        ]
    },
    {
        lead: 'We',
        rest: 'Provide',
        items: [
            'Residential & Commercial',
            'Onsite Diagnostics',
            'Customer Support',
            'Free Estimates',
            'All Sized Projects'
        ]
    },
    {
        lead: 'We',
        rest: 'Offer',
        items: [
            'Online Payments',
            'Customer Portal',
            'Online Scheduling',
            'Same Day Service',
            'Online Invoicing'
        ]
    }
];

export const process = [
    {
        step: '01',
        title: 'One call',
        body: 'A single point of contact scopes the whole electrical package (service, distribution, lighting and low voltage) instead of three separate conversations that have to be reconciled later.'
    },
    {
        step: '02',
        title: 'Engineered on paper',
        body: 'Load calculations, panel schedules and as-builts are produced before anyone pulls a wire. Conflicts get resolved in the drawing set, where they cost nothing, rather than in the field.'
    },
    {
        step: '03',
        title: 'One crew accountable',
        body: 'Rough-in, trim and commissioning are the same company, working from the same live documentation. Fewer trips, less downtime, and one number to call when something needs looking at.'
    }
];

export const advantages = [
    {
        title: 'Licensed and bonded',
        body: 'Fully licensed and bonded to deliver electrical services across Florida, with the insurance and documentation a general contractor needs on file before you break ground.'
    },
    {
        title: 'High voltage to low',
        body: 'Service entrances, switchgear and distribution through to lighting control and device trim. One contractor from the utility connection to the last cover plate.'
    },
    {
        title: 'Executive experience',
        body: 'Our leadership has delivered electrical scope on landmark South Florida towers (Porsche Design, Ritz-Carlton Residences, Armani Casa) and brings that programme discipline to every job.'
    },
    {
        title: 'Technology-forward',
        body: 'Estimating, procurement, field reporting and cost tracking run on one connected system, so material constraints and schedule risk surface early rather than at the end of the month.'
    },
    {
        title: 'Built for code',
        body: 'South Florida inspection standards, wind load and corrosion are designed for from the start. We document for permit and for whoever maintains the building next.'
    },
    {
        title: 'Energy that pays back',
        body: 'Audits, LED retrofits, demand response and solar integration, quoted against measured consumption rather than a brochure. The savings are the reason for the work.'
    },
    {
        title: 'Backed by the group',
        body: 'Low-voltage automation and interior construction sister companies mean a wall can be opened, wired, closed and finished by one organisation, on one schedule.'
    },
    {
        title: 'Same-day when it matters',
        body: 'Emergency response and diagnostic service across the tri-county area. Most issues are diagnosed, troubleshot and repaired on the same visit.'
    }
];

/* CRA inlines REACT_APP_* at build time. If the licence is not set the old
   copy rendered the literal string "Our license number is undefined.", so the
   sentence is only added when there is a number to add. */
const licence = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ELECTRIC_LICENCE) || null;

export const faqs = [
    {
        question: 'Do you provide same-day services?',
        answer: 'Yes, we strive to offer same-day service whenever possible. Depending on the job’s scope and your location, we aim to be there promptly. For emergencies, our team is ready to address electrical issues as quickly as needed.'
    },
    {
        question: 'How fast can I get a quote?',
        answer: 'For inquiries during business hours, we typically schedule a consultation within 2–3 business days. Detailed estimates are delivered within 7–10 business days. If we need any additional information, we will reach out promptly to gather the necessary details.'
    },
    {
        question: 'Are you licensed, insured, and bonded?',
        answer: 'Yes, we are fully licensed and bonded to deliver electrical services across Florida.' +
            (licence ? ` Our license number is ${licence}.` : '')
    },
    {
        question: 'Can you help reduce my electricity bill?',
        answer: 'Certainly. Our team specializes in helping clients save money by offering cost-effective solutions and optimizing electrical systems for greater energy efficiency.'
    },
    {
        question: 'How do I schedule an appointment?',
        answer: 'Scheduling is simple: visit our website to book online or call us with your preferred date, time, and details about your project. We will do our best to accommodate your request.'
    },
    {
        question: 'What areas do you serve?',
        answer: 'We provide services across South Florida, including Palm Beach County, Broward County, and Miami-Dade County.'
    }
];

export const reviews = [
    {
        name: 'Carlos Martinez',
        role: 'Panel upgrade, Fort Lauderdale',
        text: 'They replaced a 40-year-old panel and rewired half the house without turning our week upside down. Clean work, clear pricing, and they pulled the permit the same day.'
    },
    {
        name: 'Michael Johnson',
        role: 'New construction',
        text: 'Smart Electric handled the entire electrical package on our new build. The coordination with the other trades was the smoothest part of the whole project.'
    },
    {
        name: 'Avi Levi',
        role: 'Commercial fit-out',
        text: 'We needed lighting and power for a retail buildout on a hard opening date. They hit it, and the inspection passed first time.'
    },
    {
        name: 'Daniel Rodriguez',
        role: 'LED retrofit',
        text: 'The LED retrofit paid for itself faster than they projected. They measured our actual usage first instead of quoting from a catalogue.'
    },
    {
        name: 'David Cohen',
        role: 'Generator installation',
        text: 'Standby generator installed, tested and commissioned in two days. They walked us through the transfer switch and left proper documentation.'
    },
    {
        name: 'James Wilson',
        role: 'Service call',
        text: 'Called about a breaker that kept tripping. Diagnosed and repaired the same afternoon, and they explained exactly what had been wrong.'
    },
    {
        name: 'Juan Perez',
        role: 'EV charging',
        text: 'Two EV chargers installed in the garage with a subpanel to support them. Tidy conduit runs and no surprises on the invoice.'
    },
    {
        name: 'Aaron Goldstein',
        role: 'Code corrections',
        text: 'We had open violations from the city. They diagnosed everything, corrected it, and dealt with the re-inspection themselves.'
    }
];

/* The footer's group row: The Smart Solutions Group first, then the two
   sister companies. This site is deliberately absent; a card that says
   "you are here" is a dead end in the one place on the page whose whole
   job is to send the reader somewhere else, so the slot goes to the
   group instead. */
export const sisters = [
    {
        id: 'group',
        name: 'The Smart Solutions Group',
        short: 'The Group',
        accent: '#f5f5f7',
        url: 'https://thesmartsolutionsgroup.net/',
        logo: groupLogo,
        /* A square badge with no type in it, unlike the two wordmarks,
           so the footer sets the name in type beside it. */
        badge: true
    },
    {
        id: 'technology',
        name: 'Smart Technology Solutions',
        short: 'Technology',
        accent: '#A3E7FC',
        url: 'https://smart-tech.solutions/',
        logo: techLogo
    },
    {
        id: 'construction',
        name: 'Smart Construction Solutions',
        short: 'Construction',
        accent: '#FDB863',
        url: 'https://smartconstruction.solutions/',
        logo: constructionLogo
    }
];

/* The seam under the hero: this site's colour first, then its two
   sisters', which is the group's "three companies, one standard"
   resolve read from inside one of them. */
export const seam = ['#FDE382', '#A3E7FC', '#FDB863'];
